/**
 * ApprovalQueueTable Component
 *
 * Table view for guide approval queue with sortable columns, filters,
 * and actions (approve, reject, request information).
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-146 - Guide Approval Workflow Enhancement (34 SP)
 */

import {
  CheckCircle,
  XCircle,
  Clock,
  ChevronUp,
  ChevronDown,
  Eye,
  Mail,
  Ban
} from 'lucide-react';
import { useState } from 'react';
import StatusBadge from '../common/StatusBadge';
import Badge from '../common/Badge';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';

/**
 * Map approval status to StatusBadge variant
 */
function getStatusVariant(status) {
  const variants = {
    pending: 'warning',
    pending_information: 'info',
    pending_verification: 'info',
    pending_licensing: 'info',
    pending_insurance: 'info',
    pending_training: 'info',
    conditionally_approved: 'success',
    approved: 'success',
    rejected: 'error',
    suspended: 'neutral',
  };
  return variants[status] || 'neutral';
}

/**
 * Format status for display
 */
function formatStatus(status) {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Sort icon component
 */
function SortIcon({ field, sortField, sortOrder }) {
  if (field !== sortField) {
    return (
      <div className="inline-flex flex-col ml-1 opacity-30">
        <ChevronUp className="w-3 h-3 -mb-1" />
        <ChevronDown className="w-3 h-3" />
      </div>
    );
  }

  return sortOrder === 'asc' ? (
    <ChevronUp className="w-4 h-4 ml-1 inline" />
  ) : (
    <ChevronDown className="w-4 h-4 ml-1 inline" />
  );
}

export default function ApprovalQueueTable({
  guides,
  sortField,
  sortOrder,
  onSort,
  onViewGuide,
  onApprove,
  onReject,
  onRequestInfo,
  onSuspend,
  loading = false,
}) {
  const [hoveredRow, setHoveredRow] = useState(null);

  /**
   * Sortable column header
   */
  const SortableHeader = ({ field, label, className = '' }) => (
    <th
      onClick={() => onSort(field)}
      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none ${className}`}
    >
      <div className="flex items-center">
        {label}
        <SortIcon field={field} sortField={sortField} sortOrder={sortOrder} />
      </div>
    </th>
  );

  if (loading) {
    return <LoadingSpinner message="Loading approval queue..." />;
  }

  if (!guides || guides.length === 0) {
    return (
      <EmptyState
        icon={Clock}
        title="No guides in approval queue"
        description="All guides have been processed"
      />
    );
  }

  return (
    <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <SortableHeader field="guide_name" label="Guide" />
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Contact
            </th>
            <SortableHeader field="approval_status" label="Status" />
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Requirements
            </th>
            <SortableHeader field="profile_created_at" label="Submitted" />
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Reviewer
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {guides.map((guide) => (
            <tr
              key={guide.guide_id}
              onMouseEnter={() => setHoveredRow(guide.guide_id)}
              onMouseLeave={() => setHoveredRow(null)}
              className={`transition-colors ${
                hoveredRow === guide.guide_id ? 'bg-gray-50' : ''
              }`}
            >
              {/* Guide Name */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {guide.guide_name}
                    </div>
                    <div className="text-sm text-gray-500">ID: {guide.guide_id.substring(0, 8)}...</div>
                  </div>
                </div>
              </td>

              {/* Contact Info */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{guide.guide_email}</div>
                {guide.guide_phone && (
                  <div className="text-sm text-gray-500">{guide.guide_phone}</div>
                )}
              </td>

              {/* Status */}
              <td className="px-6 py-4 whitespace-nowrap">
                <StatusBadge variant={getStatusVariant(guide.status)}>
                  {formatStatus(guide.status)}
                </StatusBadge>
                {guide.conditional_expires_at && (
                  <div className="text-xs text-gray-500 mt-1">
                    Expires: {new Date(guide.conditional_expires_at).toLocaleDateString()}
                  </div>
                )}
              </td>

              {/* Requirements */}
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <div className="space-y-1">
                  {guide.requirements.pending > 0 && (
                    <Badge color="yellow">
                      {guide.requirements.pending} pending
                    </Badge>
                  )}
                  {guide.requirements.urgent > 0 && (
                    <Badge color="red">
                      {guide.requirements.urgent} urgent
                    </Badge>
                  )}
                  {guide.requirements.submitted > 0 && (
                    <Badge color="blue">
                      {guide.requirements.submitted} submitted
                    </Badge>
                  )}
                </div>
                {guide.earliest_due_date && (
                  <div className="text-xs text-gray-500 mt-1">
                    Due: {new Date(guide.earliest_due_date).toLocaleDateString()}
                  </div>
                )}
              </td>

              {/* Submitted Date */}
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(guide.created_at).toLocaleDateString()}
                <div className="text-xs text-gray-400">
                  {new Date(guide.created_at).toLocaleTimeString()}
                </div>
              </td>

              {/* Reviewer */}
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {guide.reviewer ? (
                  <div>
                    <div className="font-medium text-gray-900">{guide.reviewer.name}</div>
                    <div className="text-xs">{guide.reviewer.email}</div>
                  </div>
                ) : (
                  <span className="text-gray-400">Not assigned</span>
                )}
              </td>

              {/* Actions */}
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => onViewGuide(guide.guide_id)}
                    className="text-brand-primary hover:text-brand-secondary transition-colors"
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onApprove(guide.guide_id)}
                    disabled={!guide.stripe_onboarding_complete}
                    className="text-green-600 hover:text-green-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title={guide.stripe_onboarding_complete ? 'Approve' : 'Payment settings not complete'}
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onReject(guide.guide_id)}
                    className="text-red-600 hover:text-red-800 transition-colors"
                    title="Reject"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onRequestInfo(guide.guide_id)}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                    title="Request Information"
                  >
                    <Mail className="w-4 h-4" />
                  </button>
                  {guide.status === 'approved' && onSuspend && (
                    <button
                      onClick={() => onSuspend(guide.guide_id)}
                      className="text-gray-600 hover:text-gray-800 transition-colors"
                      title="Suspend"
                    >
                      <Ban className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
